import React, { useState, useContext } from 'react';
import { Popover, Button, Upload, message, Select, Tooltip } from 'antd';
import { UploadOutlined, CalendarOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Context } from '../context';
import { formatDate, getLanguageName, formatRelativeTime } from '../utils';


const STATUSES = {
  'done': {
    title: 'Готово',
    color: 'green'
  },
  'in_progress': {
    title: 'В процессе',
    color: 'orange'
  },
  'pending': {
    title: 'Не назначен',
    color: 'gray'
  }
}

const TaskCellComponent = ({
  task,
  taskType,
  status,
  date,
  width,
  taskTypeName,
  projectId,
  partId,
  languageId
}) => {
  const { people, updateTaskStatus, updateTaskAssignee } = useContext(Context);
  const [selectedUserId, setSelectedUserId] = useState(task?.assignee?.id || null);
  const [selectedStatus, setSelectedStatus] = useState(task?.status || null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isStatusPopoverOpen, setIsStatusPopoverOpen] = useState(false);
  // Helper function to parse groupName (comma-separated string to array)
  const parseGroupName = (groupName) => {
    if (!groupName) return [];
    return groupName.split(',').map(g => g.trim()).filter(Boolean);
  };

  // Группа по умолчанию соответствует языку задачи
  const defaultGroup = languageId ? getLanguageName(languageId) : null;
  const [selectedGroup, setSelectedGroup] = useState(defaultGroup);

  // Получаем уникальные группы из списка пользователей (учитываем множественные группы)
  const uniqueGroups = [...new Set(
    people
      .flatMap(p => parseGroupName(p.groupName))
  )];

  // Фильтруем пользователей по выбранной группе (проверяем, входит ли группа в список групп пользователя)
  const filteredPeople = selectedGroup
    ? people.filter(p => parseGroupName(p.groupName).includes(selectedGroup))
    : people;



  // Helper function to get status text
  const getStatusText = (status) => {
    return STATUSES[status]?.title || '—';
  };

  // Helper function to get status class
  const getStatusClass = (status) => {
    switch (status) {
      case 'done': return 't_status--done';
      case 'in_progress': return 't_status--progress';
      default: return '';
    }
  };

  const handleStatusChange = async (status) => {
    try {
      if (!projectId || !partId || !languageId || !taskType) {
        console.error('Missing required props for status update:', { projectId, partId, languageId, taskType });
        return;
      }

      // Передаём текущие данные задачи для сохранения id существующего статуса
      await updateTaskStatus(projectId, partId, languageId, taskType, status, selectedUserId, task);
      setSelectedStatus(status);
      message.success('Статус успешно обновлен');
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Ошибка при обновлении статуса');
    }
  };

  const onStatusChange = (status) => {
    handleStatusChange(status);
  }

  const onUserChange = async (userId) => {
    try {
      if (!projectId || !partId || !languageId || !taskType) {
        console.error('Missing required props for assignee update:', { projectId, partId, languageId, taskType });
        return;
      }

      // Передаём текущие данные задачи для сохранения id существующего статуса
      await updateTaskAssignee(projectId, partId, languageId, taskType, userId, task);
      message.success('Исполнитель успешно назначен');
    } catch (error) {
      console.error('Error updating assignee:', error);
      message.error('Ошибка при назначении исполнителя');
    }
  }


  // Helper function to find user in people list by ID
  const findUserInPeople = (assignee) => {
    if (!assignee) return null;
    const userId = typeof assignee === 'object' ? assignee.id : assignee;
    return people.find(p => p.id === userId);
  };

  // Helper function to get user initials
  const getUserInitials = (user) => {
    if (!user) return '?';
    // First try to find user in people list to get the real name
    const personFromList = findUserInPeople(user);
    if (personFromList?.name) {
      return personFromList.name.charAt(0).toUpperCase();
    }
    if (user.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user.email) {
      return user.email.split('@')[0].charAt(0).toUpperCase();
    }
    return '?';
  };

  // Helper function to get user display name
  const getUserDisplayName = (user) => {
    if (!user) return 'Unknown';
    // First try to find user in people list to get the real name
    const personFromList = findUserInPeople(user);
    if (personFromList?.name) {
      return personFromList.name;
    }
    return user.name || user.email?.split('@')[0] || 'Unknown';
  };

  // Helper function to get task type name
  const getTaskTypeName = (taskType) => {
    const names = {
      'auto_translation': 'Автоперевод',
      'translation_proofreading': 'Проверка перевода',
      'voicing': 'Озвучка',
      'voicing_proofreading': 'Проверка озвучки',
      'animations_video': 'Анимация',
      'animations_video_proofreading': 'Проверка анимации',
      'video': 'Видео',
      'video_proofreading': 'Проверка видео',
      'full_video': 'Полное видео',
      'full_video_proofreading': 'Проверка полного видео',
      'ts_playlist': 'Плейлисты',
      'loading_in_s3': 'Загрузка в S3'
    };
    return names[taskType] || taskType;
  };

  const taskTypeDisplay = taskTypeName || getTaskTypeName(taskType);

  // Helper function to check if user has changed
  const hasUserChanged = () => {
    const currentUserId = task?.assignee?.id || null;
    return selectedUserId !== currentUserId;
  };

  // Helper function to handle user selection
  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
  };

  // Helper function to handle OK button
  const handleOk = () => {
    onUserChange?.(selectedUserId);
    setIsPopoverOpen(false);
    setSelectedUserId(task?.assignee?.id || null);
  };

  // Helper function to handle Cancel button
  const handleCancel = () => {
    setSelectedUserId(task?.assignee?.id || null);
    setSelectedGroup(defaultGroup); // Возвращаем группу по языку
    setIsPopoverOpen(false);
  };

  // Helper function to create popover content
  const getPopoverContent = () => {
    return (
      <div className="_fv _fgap1" style={{
        marginTop: '-12px'
      }}>
        <div className="_fv _fgap02">
          <h4>Выберите пользователя</h4>

          {/* Выбор группы */}
          {uniqueGroups.length > 0 && (
            <div style={{ marginBottom: '8px' }}>
              <div style={{ marginBottom: '4px', fontSize: '12px', color: '#888' }}>
                Группа:
              </div>
              <Select
                value={selectedGroup}
                onChange={(value) => {
                  setSelectedGroup(value);
                  setSelectedUserId(null); // сбрасываем выбранного пользователя при смене группы
                }}
                style={{ width: '100%' }}
                allowClear
                placeholder="Все группы"
                options={[
                  { value: null, label: 'Все группы' },
                  ...uniqueGroups.map(group => ({
                    value: group,
                    label: group
                  }))
                ]}
              />
            </div>
          )}

          <div className="_f _fwrap _fgap0" style={{
            width: '28.8em',
            backgroundColor: 'white',
            borderRadius: '4px',
            padding: '0.2em 0.4em',
            marginLeft: '-8px',
            marginRight: '-8px',
            boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.14)'
          }}>
            {filteredPeople.map(user => (
              <div
                key={user.id}
                onClick={() => handleUserSelect(user.id)}
                style={{
                  padding: '4px 4px',
                  cursor: 'pointer',
                  backgroundColor: selectedUserId === user.id ? '#e6f7ff' : 'transparent',
                  border: selectedUserId === user.id ? '1px solid #1890ff' : '1px solid transparent',
                  borderRadius: '4px',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '14em'
                }}
              >
                <div className="t_user_ava">
                  <div>{getUserInitials(user)}</div>
                </div>
                <span>{getUserDisplayName(user)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="_f _fm _fgap04">
          <Button size="small" onClick={handleCancel}>
            Отмена
          </Button>
          <Button
            size="small"
            type="primary"
            onClick={handleOk}
            disabled={!hasUserChanged()}
          >
            Назначить
          </Button>
        </div>
      </div>
    );
  };

  // Helper function to create status popover content
  const getStatusPopoverContent = () => {
    return (
      <div className="_fv _fgap1" style={{
        marginTop: '-12px'
      }}>
        <div className="_fv _fgap0">
          <h4>Изменить статус</h4>
          <div className="_fv _fgap06">
            <div className="_f _fwrap _fgap0" style={{
              width: '16em',
              backgroundColor: 'white',
              borderRadius: '4px',
              padding: '4px',
              marginLeft: '-8px',
              marginRight: '-8px',
              boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.14)'
            }}>
              {Object.entries(STATUSES).map(([status, config]) => (
                <div
                  className="_fv _fgap0"
                  key={status}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedStatus(status);
                    // Handle status change here if needed
                    // setIsStatusPopoverOpen(false);
                  }}
                  style={{
                    padding: '8px',
                    cursor: 'pointer',
                    backgroundColor: selectedStatus === status ? '#e6f7ff' : 'transparent',
                    border: selectedStatus === status ? '1px solid #1890ff' : '1px solid transparent',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%'
                  }}
                >
                  <div className={`t_status _click ${getStatusClass(status)}`}>
                    {config.title}
                  </div>
                </div>
              ))}
            </div>
            <div className="_f _fm _fgap06">
              <Button size="small" onClick={() => setIsStatusPopoverOpen(false)}>
                Отмена
              </Button>
              <Button
                size="small"
                type="primary"
                onClick={async () => {
                  setIsStatusPopoverOpen(false);
                  await handleStatusChange(selectedStatus);
                }}
                disabled={selectedStatus === status}
              >
                Применить
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const hideAssignee = taskType === 'auto_translation' || taskType === 'voicing';

  const renderTaskCellContent = () => {
    if (!task) {
      return (
        <div className="t_cell t_cell--un" style={{ width }}>
          {!hideAssignee && <div
            className={`t_user _rel`}
            style={{ zIndex: 1000 }}>
            <Popover
              content={getPopoverContent()}
              trigger="click"
              open={isPopoverOpen}
              onOpenChange={setIsPopoverOpen}
              placement="bottomLeft"
              destroyTooltipOnHide
            >
              <div
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '0 4px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div className="t_user_ava t_user_ava--undefined">
                  <div>?</div>
                </div>
                <div className="t_user_name">Назначить</div>
              </div>
            </Popover>
          </div>}
          <div className="t_status0">
            <div className="_fv _fgap0">
              <div className={`t_status _click ${getStatusClass('pending')}`}>
                <Popover
                  content={getStatusPopoverContent()}
                  trigger="click"
                  open={isStatusPopoverOpen}
                  onOpenChange={setIsStatusPopoverOpen}
                  placement="bottomLeft"
                  destroyTooltipOnHide
                >
                  <div>{STATUSES['pending'].title}</div>
                </Popover>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const isRobot = taskType === 'auto_translation';

    return (
      <div className="t_cell" style={{ width }}>
        {!hideAssignee && <div
          className={`t_user _rel`}
        >
          {isRobot ? (
            <>
              <div className="t_user_ava t_user_ava--robot">
                <div>🤖</div>
              </div>
              <div className="t_user_name">Robot</div>
            </>
          ) : (
            <Popover
              content={getPopoverContent()}
              trigger="click"
              open={isPopoverOpen}
              onOpenChange={setIsPopoverOpen}
              placement="bottomLeft"
              destroyTooltipOnHide
            >
              <div
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div className="t_user_ava">
                  <div>{getUserInitials(task.assignee)}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div className="t_user_name">
                    {getUserDisplayName(task.assignee)}
                  </div>
                  {/* Показываем completed_at для готовых задач, иначе assigned_at */}
                  {status === 'done' && task.completed_at ? (
                    <Tooltip
                      title={`Завершено: ${new Date(task.completed_at).toLocaleString('ru-RU', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}`}
                      placement="bottom"
                    >
                      <div style={{
                        fontSize: '10px',
                        color: '#52c41a',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                        cursor: 'help'
                      }}>
                        <CheckCircleOutlined style={{ fontSize: '9px' }} />
                        {formatRelativeTime(task.completed_at)}
                      </div>
                    </Tooltip>
                  ) : task.assigned_at && (
                    <Tooltip
                      title={`Назначено: ${new Date(task.assigned_at).toLocaleString('ru-RU', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}`}
                      placement="bottom"
                    >
                      <div style={{
                        fontSize: '10px',
                        color: '#8c8c8c',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                        cursor: 'help'
                      }}>
                        <CalendarOutlined style={{ fontSize: '9px' }} />
                        {formatRelativeTime(task.assigned_at)}
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
            </Popover>
          )}
        </div>}
        <div className="t_status0">
          <div className="_f _fm">
            <div className={`t_status _click ${getStatusClass(status)}`}>
              <Popover
                content={getStatusPopoverContent()}
                trigger="click"
                open={isStatusPopoverOpen}
                onOpenChange={setIsStatusPopoverOpen}
                placement="bottomLeft"
                destroyTooltipOnHide
              >
                <div>{getStatusText(status)}</div>
              </Popover>
            </div>
            <div className="t_status_date">
              <div>{formatDate(date)}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="t_line" style={{ width }}>
      <div className="t_type">
        <div>{taskTypeDisplay}</div>
      </div>
      {renderTaskCellContent()}
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export const TaskCell = React.memo(TaskCellComponent); 